import { useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { ErroApi } from '../lib/api';
import {
  arquivarCard,
  buscarCard,
  comentarCard,
  criarTarefa,
  doCampoDataHora,
  enviarArquivo,
  ETAPAS,
  formatarData,
  linkGoogleAgenda,
  removerTarefa,
  salvarCard,
  salvarTarefa,
  CORES_DA_ETAPA,
  NOME_DA_ETAPA,
  type CardCompleto,
} from '../lib/quadro';
import { Alerta, Botao } from './Interface';
import { useConfirmacao } from './Confirmacao';

type Props = {
  cardId: number;
  nomeQuadro: string;
  podeEditar: boolean;
  pessoas: { id: number; nome: string }[];
  /** listas do quadro, para mover o card sem arrastar (serve no celular) */
  colunas: { id: number; nome: string; chave: string }[];
  aoFechar: () => void;
  aoMudar: (card: CardCompleto) => void;
};

/** Data e hora separadas, no fuso de Sao Paulo. */
function partesDaEntrega(iso: string | null): { data: string; hora: string } {
  if (!iso) return { data: '', hora: '' };
  const p = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(iso));
  const m: Record<string, string> = {};
  for (const parte of p) if (parte.type !== 'literal') m[parte.type] = parte.value;
  return { data: `${m.year}-${m.month}-${m.day}`, hora: `${m.hour}:${m.minute}` };
}

export default function CardDetalhe({
  cardId,
  nomeQuadro,
  podeEditar,
  pessoas,
  colunas,
  aoFechar,
  aoMudar,
}: Props) {
  const [card, setCard] = useState<CardCompleto | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [salvando, setSalvando] = useState<string | null>(null);

  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [linkBruto, setLinkBruto] = useState('');
  const [linkPronto, setLinkPronto] = useState('');
  const [linkNome, setLinkNome] = useState('');
  const [data, setData] = useState('');
  const [hora, setHora] = useState('');

  const [comentario, setComentario] = useState('');
  const [tarefaTexto, setTarefaTexto] = useState('');
  const [tarefaQuem, setTarefaQuem] = useState('');
  const [tarefaPrioridade, setTarefaPrioridade] = useState('normal');
  const [verTudo, setVerTudo] = useState(false);
  // lista de responsaveis: abre, procura pelo nome e marca varios
  const [listaResp, setListaResp] = useState(false);
  const [buscaResp, setBuscaResp] = useState('');
  // capa aberta em tela cheia
  const [capaGrande, setCapaGrande] = useState(false);

  const descricaoRef = useRef<HTMLTextAreaElement>(null);
  const { confirmar, perguntar, dialogo } = useConfirmacao();

  useEffect(() => {
    let vivo = true;
    buscarCard(cardId)
      .then((d) => {
        if (!vivo) return;
        aplicar(d.card);
      })
      .catch((e: ErroApi) => vivo && setErro(e.message));
    return () => {
      vivo = false;
    };
  }, [cardId]);

  function aplicar(novo: CardCompleto) {
    setCard(novo);
    setTitulo(novo.titulo);
    setDescricao(novo.descricaoHtml);
    setLinkBruto(novo.linkBruto ?? '');
    setLinkPronto(novo.linkPronto ?? '');
    setLinkNome(novo.linkNome ?? '');
    const p = partesDaEntrega(novo.entregaEm);
    setData(p.data);
    setHora(p.hora);
  }

  /** Junta data e hora num instante; sem hora, marca 19:00. */
  const entregaEscolhida = useMemo(() => {
    if (!data) return null;
    return doCampoDataHora(`${data}T${hora || '19:00'}`);
  }, [data, hora]);

  /** Duas datas sao a mesma quando apontam para o mesmo instante. */
  function mesmoInstante(a: string | null, b: string | null): boolean {
    const x = a ? new Date(a).getTime() : null;
    const y = b ? new Date(b).getTime() : null;
    return x === y;
  }

  /** O que a pessoa digitou e ainda nao foi para o servidor. */
  function pendencias(atual: CardCompleto): Record<string, unknown> {
    const mudou: Record<string, unknown> = {};
    if (titulo !== atual.titulo && titulo.trim()) mudou.titulo = titulo;
    if (descricao !== atual.descricaoHtml) mudou.descricaoHtml = descricao;
    if ((linkBruto || null) !== atual.linkBruto) mudou.linkBruto = linkBruto.trim() || null;
    if ((linkPronto || null) !== atual.linkPronto) mudou.linkPronto = linkPronto.trim() || null;
    if ((linkNome || null) !== atual.linkNome) mudou.linkNome = linkNome.trim() || null;
    // compara o instante, nao o texto: assim o card nunca e regravado a toa
    if (!mesmoInstante(entregaEscolhida ?? null, atual.entregaEm)) mudou.entregaEm = entregaEscolhida;
    return mudou;
  }

  async function salvar(campo: string, mudancas: Record<string, unknown>) {
    if (!card || Object.keys(mudancas).length === 0) return;
    setErro(null);
    setSalvando(campo);
    try {
      // manda SO o que mudou, com a versao que estava na tela
      const r = await salvarCard(card.id, mudancas, card.versao);
      aplicar(r.card);
      aoMudar(r.card);
      if (r.houveMudancaExterna) {
        setAviso('Outra pessoa mexeu neste card enquanto você editava. Sua alteracao entrou e a tela já esta atualizada.');
      }
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui salvar.');
      throw e;
    } finally {
      setSalvando(null);
    }
  }

  /** Fechar (clicando fora, no X ou no botao) salva o que estiver pendente. */
  async function fecharSalvando() {
    if (card && podeEditar) {
      const mudou = pendencias(card);
      if (Object.keys(mudou).length > 0) {
        try {
          await salvar('fechando', mudou);
        } catch {
          return; // deu erro: mantem aberto para a pessoa ver o motivo
        }
      }
    }
    aoFechar();
  }

  function envolver(abre: string, fecha: string) {
    const area = descricaoRef.current;
    if (!area) return;
    const inicio = area.selectionStart ?? 0;
    const fim = area.selectionEnd ?? 0;
    const selecionado = descricao.slice(inicio, fim) || 'texto';
    const novo = descricao.slice(0, inicio) + abre + selecionado + fecha + descricao.slice(fim);
    setDescricao(novo);
    requestAnimationFrame(() => {
      area.focus();
      area.setSelectionRange(inicio + abre.length, inicio + abre.length + selecionado.length);
    });
  }

  const comentarios = card?.comentarios ?? [];
  const mostrados = verTudo ? comentarios : comentarios.slice(-3);

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-tinta-900/40 p-3 md:p-6"
      onClick={() => void fecharSalvando()}
    >
      <div
        className="w-full max-w-2xl rounded-2xl bg-white shadow-cartao"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-tinta-200 px-4 py-3 sm:px-5 sm:py-4">
          <div>
            <h2 className="text-lg font-bold text-tinta-900">
              Editar card <span className="font-normal text-tinta-400">· {nomeQuadro}</span>
            </h2>
            <p className="mt-0.5 text-sm text-tinta-400">
              Pode fechar clicando fora — o que voce escreveu e salvo sozinho.
            </p>
          </div>
          <button
            onClick={() => void fecharSalvando()}
            aria-label="Fechar"
            className="rounded-lg px-2 py-1 text-xl leading-none text-tinta-400 hover:bg-tinta-100"
          >
            &times;
          </button>
        </div>

        {!card ? (
          <div className="p-6 text-tinta-400">Carregando...</div>
        ) : (
          <div className="space-y-5 p-4 sm:p-5">
            {erro && <Alerta>{erro}</Alerta>}
            {aviso && <Alerta tipo="aviso">{aviso}</Alerta>}

            <label className="block">
              <span className="mb-1.5 block break-words text-sm font-semibold text-tinta-700">Título</span>
              <input
                value={titulo}
                disabled={!podeEditar}
                onChange={(e) => setTitulo(e.target.value)}
                onBlur={() => titulo !== card.titulo && titulo.trim() && void salvar('titulo', { titulo })}
                className="w-full rounded-xl border border-tinta-200 px-3.5 py-2.5"
              />
            </label>

            <div>
              <span className="mb-1.5 block break-words text-sm font-semibold text-tinta-700">Descrição</span>
              <div className="overflow-hidden rounded-xl border border-tinta-200">
                <div className="flex flex-wrap items-center gap-1 border-b border-tinta-200 bg-tinta-100 px-2 py-1.5">
                  {[
                    { r: 'B', abre: '<b>', fecha: '</b>', classe: 'font-bold' },
                    { r: 'I', abre: '<i>', fecha: '</i>', classe: 'italic' },
                    { r: 'S', abre: '<s>', fecha: '</s>', classe: 'line-through' },
                    { r: 'T', abre: '<h3>', fecha: '</h3>', classe: 'font-semibold' },
                    { r: '•—', abre: '<ul><li>', fecha: '</li></ul>', classe: '' },
                    { r: '1—', abre: '<ol><li>', fecha: '</li></ol>', classe: '' },
                  ].map((b) => (
                    <button
                      key={b.r}
                      type="button"
                      disabled={!podeEditar}
                      onClick={() => envolver(b.abre, b.fecha)}
                      className={`rounded px-2 py-0.5 text-sm text-tinta-700 hover:bg-white ${b.classe}`}
                    >
                      {b.r}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={!podeEditar}
                    onClick={async () => {
                      const r = await perguntar({
                        titulo: 'Colocar um link no texto',
                        texto: 'Cole o endereco. So aceitamos http e https.',
                        confirmar: 'Inserir link',
                        campo: { rotulo: 'Endereco', sugestao: 'https://', obrigatorio: true },
                      });
                      if (!r.ok) return;
                      const endereco = r.texto.trim();
                      if (!/^https?:\/\//i.test(endereco)) {
                        setErro('O link precisa comecar com http:// ou https://');
                        return;
                      }
                      envolver(`<a href="${endereco}">`, '</a>');
                    }}
                    className="rounded px-2 py-0.5 text-sm text-tinta-700 hover:bg-white"
                  >
                    🔗
                  </button>
                </div>
                <textarea
                  ref={descricaoRef}
                  rows={4}
                  disabled={!podeEditar}
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  onBlur={() =>
                    descricao !== card.descricaoHtml && void salvar('descricao', { descricaoHtml: descricao })
                  }
                  placeholder="Escreva os detalhes..."
                  className="w-full px-3.5 py-2.5 text-sm outline-none"
                />
              </div>
              {card.descricaoHtml && (
                <div
                  className="mt-2 rounded-xl bg-tinta-100 p-3 text-sm text-tinta-800"
                  // nada vai para a tela sem passar pelo DOMPurify
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(card.descricaoHtml) }}
                />
              )}
            </div>

            <div>
              <span className="mb-1.5 block break-words text-sm font-semibold text-tinta-700">
                Responsaveis <span className="font-normal text-tinta-400">({card.responsaveis.length})</span>
              </span>

              <button
                type="button"
                disabled={!podeEditar}
                onClick={() => setListaResp((v) => !v)}
                className="flex w-full items-center justify-between gap-2 rounded-xl border border-tinta-200 bg-white px-3.5 py-2.5 text-left text-sm"
              >
                <span
                  className={`min-w-0 truncate ${
                    card.responsaveis.length ? 'text-tinta-900' : 'text-tinta-400'
                  }`}
                >
                  {card.responsaveis.length
                    ? card.responsaveis.map((r) => r.nome).join(', ')
                    : 'Escolher quem e responsavel'}
                </span>
                <span className="shrink-0 text-tinta-400">▾</span>
              </button>

              {listaResp && (
                <div className="mt-1 rounded-xl border border-tinta-200 bg-white shadow-cartao">
                  <div className="border-b border-tinta-200 p-2">
                    <input
                      autoFocus
                      value={buscaResp}
                      onChange={(e) => setBuscaResp(e.target.value)}
                      placeholder="Procurar..."
                      className="w-full rounded-lg border border-tinta-200 px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2 border-b border-tinta-200 px-3 py-2 text-xs">
                    <button
                      type="button"
                      disabled={!podeEditar || salvando === 'responsaveis'}
                      onClick={() =>
                        void salvar('responsaveis', { responsaveis: pessoas.map((p) => p.id) })
                      }
                      className="font-semibold text-tinta-900 hover:underline"
                    >
                      Selecionar todos
                    </button>
                    <button
                      type="button"
                      disabled={!podeEditar || salvando === 'responsaveis'}
                      onClick={() => void salvar('responsaveis', { responsaveis: [] })}
                      className="text-tinta-400 hover:text-tinta-900"
                    >
                      Limpar
                    </button>
                  </div>
                  <ul className="max-h-60 overflow-y-auto py-1">
                    {pessoas
                      .filter((p) => p.nome.toLowerCase().includes(buscaResp.trim().toLowerCase()))
                      .map((p) => {
                        const marcado = card.responsaveis.some((r) => r.id === p.id);
                        return (
                          <li key={p.id}>
                            <button
                              type="button"
                              disabled={!podeEditar || salvando === 'responsaveis'}
                              onClick={() => {
                                const ids = marcado
                                  ? card.responsaveis.filter((r) => r.id !== p.id).map((r) => r.id)
                                  : [...card.responsaveis.map((r) => r.id), p.id];
                                void salvar('responsaveis', { responsaveis: ids });
                              }}
                              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${
                                marcado ? 'bg-tinta-900 text-white' : 'text-tinta-700 hover:bg-tinta-100'
                              }`}
                            >
                              <span className="w-4 shrink-0 text-center">{marcado ? '✓' : ''}</span>
                              <span className="truncate">{p.nome}</span>
                            </button>
                          </li>
                        );
                      })}
                    {pessoas.length === 0 && (
                      <li className="px-3 py-2 text-sm text-tinta-400">Ninguem na lista ainda.</li>
                    )}
                  </ul>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1.5 block break-words text-sm font-semibold text-tinta-700">Data de entrega</span>
                <input
                  type="date"
                  disabled={!podeEditar}
                  value={data}
                  onChange={(e) => setData(e.target.value)}
                  onBlur={() => {
                    const novo = data ? doCampoDataHora(`${data}T${hora || '19:00'}`) : null;
                    if (!mesmoInstante(novo, card.entregaEm)) void salvar('entrega', { entregaEm: novo });
                  }}
                  className="w-full rounded-xl border border-tinta-200 px-3.5 py-2.5"
                />
              </label>
              <label className="block">
                <span className="mb-1.5 block break-words text-sm font-semibold text-tinta-700">Horário</span>
                <input
                  type="time"
                  disabled={!podeEditar || !data}
                  value={hora}
                  onChange={(e) => setHora(e.target.value)}
                  onBlur={() => {
                    if (!data) return;
                    const novo = doCampoDataHora(`${data}T${hora || '19:00'}`);
                    if (!mesmoInstante(novo, card.entregaEm)) void salvar('entrega', { entregaEm: novo });
                  }}
                  className="w-full rounded-xl border border-tinta-200 px-3.5 py-2.5"
                />
                <span className="mt-1 block text-xs text-tinta-400">Horário de Brasilia</span>
              </label>
            </div>

            <label className="block">
              <span className="mb-1.5 block break-words text-sm font-semibold text-tinta-700">Lista</span>
              <select
                value={card.colunaChave}
                disabled={!podeEditar}
                onChange={(e) => void salvar('coluna', { colunaChave: e.target.value })}
                className="w-full rounded-xl border border-tinta-200 bg-white px-3.5 py-2.5"
              >
                {colunas.map((c) => (
                  <option key={c.id} value={c.chave}>
                    {c.nome}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-xs text-tinta-400">
                Trocar a lista aqui move o card no quadro.
              </span>
            </label>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1.5 block break-words text-sm font-semibold text-tinta-700">Etapa</span>
                <select
                  value={card.etapa}
                  disabled={!podeEditar}
                  onChange={(e) => void salvar('etapa', { etapa: e.target.value })}
                  className="w-full rounded-xl border border-tinta-200 bg-white px-3.5 py-2.5"
                >
                  {ETAPAS.map((e) => (
                    <option key={e.valor} value={e.valor}>
                      {e.nome}
                    </option>
                  ))}
                </select>
                <span
                  className={`mt-2 inline-block rounded-lg px-2 py-0.5 text-xs font-semibold ${
                    CORES_DA_ETAPA[card.etapa] ?? 'bg-tinta-100 text-tinta-600'
                  }`}
                >
                  {NOME_DA_ETAPA[card.etapa] ?? card.etapa}
                </span>
              </label>
              <label className="block">
                <span className="mb-1.5 block break-words text-sm font-semibold text-tinta-700">Prioridade</span>
                <select
                  value={card.prioridade}
                  disabled={!podeEditar}
                  onChange={(e) => void salvar('prioridade', { prioridade: e.target.value })}
                  className="w-full rounded-xl border border-tinta-200 bg-white px-3.5 py-2.5"
                >
                  <option value="normal">Normal</option>
                  <option value="urgente">Urgente</option>
                </select>
              </label>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <span className="mb-1.5 block break-words text-sm font-semibold text-tinta-700">
                  Link bruto (gravacao/originais)
                </span>
                <input
                  value={linkBruto}
                  disabled={!podeEditar}
                  placeholder="https://drive.google.com/..."
                  onChange={(e) => setLinkBruto(e.target.value)}
                  onBlur={() =>
                    (linkBruto || null) !== card.linkBruto &&
                    void salvar('linkBruto', { linkBruto: linkBruto.trim() || null })
                  }
                  className="w-full rounded-xl border border-tinta-200 px-3.5 py-2.5"
                />
                {card.linkBruto && (
                  <a
                    href={card.linkBruto}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="mt-1 inline-block text-xs font-medium text-tinta-900 hover:underline"
                  >
                    🔗 Abrir arquivo bruto
                  </a>
                )}
              </div>
              <div>
                <span className="mb-1.5 block break-words text-sm font-semibold text-tinta-700">
                  Link do arquivo pronto
                </span>
                <input
                  value={linkPronto}
                  disabled={!podeEditar}
                  placeholder="https://drive.google.com/..."
                  onChange={(e) => setLinkPronto(e.target.value)}
                  onBlur={() =>
                    (linkPronto || null) !== card.linkPronto &&
                    void salvar('linkPronto', { linkPronto: linkPronto.trim() || null })
                  }
                  className="w-full rounded-xl border border-tinta-200 px-3.5 py-2.5"
                />
                <span className="mt-1 block text-xs text-tinta-400">
                  Ao preencher, o card vai para "Prontos".
                </span>
              </div>
            </div>

            <label className="block">
              <span className="mb-1.5 block break-words text-sm font-semibold text-tinta-700">
                Nome do link (opcional)
              </span>
              <input
                value={linkNome}
                disabled={!podeEditar}
                placeholder="Ex.: Vídeo final"
                onChange={(e) => setLinkNome(e.target.value)}
                onBlur={() =>
                  (linkNome || null) !== card.linkNome &&
                  void salvar('linkNome', { linkNome: linkNome.trim() || null })
                }
                className="w-full rounded-xl border border-tinta-200 px-3.5 py-2.5"
              />
            </label>

            {card.entregaEm && (
              <a
                href={linkGoogleAgenda(card.titulo, card.entregaEm, card.linkPronto ?? undefined)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl border border-tinta-200 px-3.5 py-2 text-sm font-medium text-tinta-700 hover:bg-tinta-100"
              >
                📅 Adicionar ao Google Agenda
              </a>
            )}

            <div>
              <span className="mb-1.5 block break-words text-sm font-semibold text-tinta-700">Imagem de capa</span>
              {card.capaCaminho ? (
                <div className="space-y-3">
                {/* a capa aparece no formato dela; clicar abre em tamanho grande */}
                  <button
                    type="button"
                    onClick={() => setCapaGrande(true)}
                    title="Ver a capa em tamanho grande"
                    className="block w-full overflow-hidden rounded-xl border border-tinta-200 bg-tinta-100"
                  >
                    <img
                      src={`/api/uploads/${card.capaCaminho}`}
                      alt="Capa"
                      className="mx-auto block h-auto max-h-72 w-auto max-w-full object-contain"
                    />
                  </button>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setCapaGrande(true)}
                      className="rounded-xl border border-tinta-200 px-3 py-1.5 text-sm font-medium text-tinta-900 hover:bg-tinta-100"
                    >
                      ⤢ Ver grande
                    </button>
                  <a
                    href={`/api/uploads/${card.capaCaminho}`}
                    download
                    className="rounded-xl border border-tinta-200 px-3 py-1.5 text-sm font-medium text-tinta-900 hover:bg-tinta-100"
                  >
                    ↓ Baixar imagem
                  </a>
                    {podeEditar && (
                      <>
                        <label className="cursor-pointer rounded-xl border border-tinta-200 px-3 py-1.5 text-sm text-tinta-700 hover:bg-tinta-100">
                          {salvando === 'capa' ? 'Enviando...' : 'Trocar imagem'}
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/gif"
                            className="hidden"
                            onChange={(e) => void enviarCapa(e)}
                          />
                        </label>
                        <button
                          onClick={() => void salvar('capa', { capaCaminho: null })}
                          className="text-sm text-red-600 hover:underline"
                        >
                          Remover capa
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                podeEditar && (
                  <label className="flex cursor-pointer items-center justify-center rounded-xl border border-dashed border-tinta-200 px-3 py-6 text-sm text-tinta-400 hover:border-tinta-900 hover:text-tinta-900">
                    {salvando === 'capa' ? 'Enviando...' : 'Escolher imagem (até 25 MB)'}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      className="hidden"
                      onChange={(e) => void enviarCapa(e)}
                    />
                  </label>
                )
              )}
            </div>

            <div className="border-t border-tinta-200 pt-4">
              <h3 className="text-sm font-semibold text-tinta-700">Tarefas</h3>
              <p className="text-xs text-tinta-400">
                Divida o card por pessoa — ex.: "Editar vídeo" para um, "Aprovar" para outro.
              </p>

              {card.tarefas.length === 0 ? (
                <p className="mt-2 text-sm text-tinta-400">Nenhuma tarefa ainda.</p>
              ) : (
                <ul className="mt-2 space-y-1">
                  {card.tarefas.map((t) => (
                    <li
                      key={t.id}
                      className="flex flex-wrap items-center gap-2 rounded-xl bg-tinta-100 px-3 py-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={t.feito}
                        disabled={!podeEditar}
                        onChange={async () => {
                          try {
                            const r = await salvarTarefa(t.id, { feito: !t.feito });
                            aplicar(r.card);
                            aoMudar(r.card);
                          } catch (e) {
                            setErro(e instanceof ErroApi ? e.message : 'Não consegui salvar a tarefa.');
                          }
                        }}
                      />
                      <span
                        className={`min-w-0 break-words ${
                          t.feito ? 'text-tinta-400 line-through' : 'text-tinta-800'
                        }`}
                      >
                        {t.texto}
                      </span>
                      {t.responsavelNome && (
                        <span className="rounded-full bg-white px-2 py-0.5 text-xs text-tinta-600">
                          {t.responsavelNome}
                        </span>
                      )}
                      {t.prioridade === 'urgente' && (
                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                          urgente
                        </span>
                      )}
                      {podeEditar && (
                        <button
                          onClick={async () => {
                            try {
                              const r = await removerTarefa(t.id);
                              aplicar(r.card);
                              aoMudar(r.card);
                            } catch (e) {
                              setErro(e instanceof ErroApi ? e.message : 'Não consegui remover.');
                            }
                          }}
                          className="ml-auto text-xs text-tinta-400 hover:text-red-600"
                        >
                          remover
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {podeEditar && (
                // flex com quebra: nada escapa da borda do card no celular
                <div className="mt-3 flex flex-wrap gap-2">
                  <input
                    value={tarefaTexto}
                    onChange={(e) => setTarefaTexto(e.target.value)}
                    placeholder="O que precisa ser feito..."
                    className="w-full min-w-0 flex-1 rounded-xl border border-tinta-200 px-3 py-2 text-sm sm:w-auto"
                  />
                  <select
                    value={tarefaQuem}
                    onChange={(e) => setTarefaQuem(e.target.value)}
                    className="min-w-0 flex-1 rounded-xl border border-tinta-200 bg-white px-3 py-2 text-sm sm:flex-none"
                  >
                    <option value="">Quem faz?</option>
                    {pessoas.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.nome}
                      </option>
                    ))}
                  </select>
                  <select
                    value={tarefaPrioridade}
                    onChange={(e) => setTarefaPrioridade(e.target.value)}
                    className="min-w-0 flex-1 rounded-xl border border-tinta-200 bg-white px-3 py-2 text-sm sm:flex-none"
                  >
                    <option value="normal">Normal</option>
                    <option value="urgente">Urgente</option>
                  </select>
                  <Botao
                    onClick={async () => {
                      if (!tarefaTexto.trim()) return;
                      try {
                        const r = await criarTarefa(card.id, {
                          texto: tarefaTexto.trim(),
                          responsavelId: tarefaQuem ? Number(tarefaQuem) : null,
                          prioridade: tarefaPrioridade,
                        });
                        aplicar(r.card);
                        aoMudar(r.card);
                        setTarefaTexto('');
                      } catch (e) {
                        setErro(e instanceof ErroApi ? e.message : 'Não consegui criar a tarefa.');
                      }
                    }}
                  >
                    Adicionar
                  </Botao>
                </div>
              )}
            </div>

            <div className="border-t border-tinta-200 pt-4">
              <h3 className="text-sm font-semibold text-tinta-700">Comentarios</h3>
              <p className="text-xs text-tinta-400">
                Toda alteracao no card entra aqui automaticamente, escrita pelo servidor.
              </p>

              {podeEditar && (
                <div className="mt-3 flex gap-2">
                  <input
                    value={comentario}
                    onChange={(e) => setComentario(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && void enviarComentario()}
                    placeholder="Escreva um comentario ou cole um link..."
                    className="min-w-0 flex-1 rounded-xl border border-tinta-200 px-3 py-2 text-sm"
                  />
                  <Botao carregando={salvando === 'comentario'} onClick={() => void enviarComentario()}>
                    Comentar
                  </Botao>
                </div>
              )}

              {comentarios.length > 3 && !verTudo && (
                <button
                  onClick={() => setVerTudo(true)}
                  className="mt-3 w-full rounded-xl border border-dashed border-tinta-200 py-2 text-sm text-tinta-400 hover:text-tinta-900"
                >
                  Ver mais {comentarios.length - 3} registros
                </button>
              )}

              <ul className="mt-3 space-y-2">
                {mostrados.map((c) => (
                  <li key={c.id} className="rounded-xl border border-tinta-200 px-3 py-2 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`rounded px-1.5 py-0.5 text-xs font-semibold ${
                          c.tipo === 'sistema' ? 'bg-tinta-100 text-tinta-600' : 'bg-tinta-900 text-white'
                        }`}
                      >
                        {c.tipo === 'sistema' ? 'registro' : 'comentario'}
                      </span>
                      {c.autor && <b className="text-tinta-900">{c.autor}</b>}
                      <span className="text-xs text-tinta-400">{formatarData(c.criadoEm)}</span>
                    </div>
                    <p className="mt-1 break-words text-tinta-700">{c.texto}</p>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-tinta-200 pt-4">
              {podeEditar ? (
                <button
                  onClick={() => void arquivar()}
                  className="rounded-xl border border-red-200 px-3.5 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
                >
                  Excluir
                </button>
              ) : (
                <span />
              )}
              <div className="flex items-center gap-2">
                <Botao variante="secundario" onClick={() => void fecharSalvando()}>
                  Fechar
                </Botao>
                {podeEditar && (
                  <Botao
                    carregando={salvando !== null}
                    onClick={() => void fecharSalvando()}
                  >
                    Salvar
                  </Botao>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* capa em tela cheia: clicar em qualquer lugar fecha */}
      {capaGrande && card?.capaCaminho && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-tinta-900/90 p-3 md:p-8"
          onClick={(e) => {
            e.stopPropagation();
            setCapaGrande(false);
          }}
        >
          <img
            src={`/api/uploads/${card.capaCaminho}`}
            alt="Capa"
            className="max-h-full max-w-full rounded-xl object-contain"
          />
          <button
            type="button"
            aria-label="Fechar imagem"
            onClick={(e) => {
              e.stopPropagation();
              setCapaGrande(false);
            }}
            className="absolute right-3 top-3 rounded-xl bg-white/15 px-3 py-1.5 text-xl leading-none text-white hover:bg-white/25"
          >
            &times;
          </button>
        </div>
      )}
      {dialogo}
    </div>
  );

  async function enviarCapa(e: React.ChangeEvent<HTMLInputElement>) {
    const arquivo = e.target.files?.[0];
    e.target.value = '';
    if (!arquivo || !card) return;
    setErro(null);
    setSalvando('capa');
    try {
      const enviado = await enviarArquivo(arquivo);
      await salvar('capa', { capaCaminho: enviado.caminho });
    } catch (erroEnvio) {
      setErro(erroEnvio instanceof Error ? erroEnvio.message : 'Não consegui enviar o arquivo.');
      setSalvando(null);
    }
  }

  async function enviarComentario() {
    if (!card || !comentario.trim()) return;
    setSalvando('comentario');
    try {
      const r = await comentarCard(card.id, comentario.trim());
      aplicar(r.card);
      setComentario('');
      setVerTudo(true);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui comentar.');
    } finally {
      setSalvando(null);
    }
  }

  async function arquivar() {
    if (!card) return;
    const ok = await confirmar({
      titulo: 'Excluir este card?',
      texto: 'Ele sai do quadro, mas o histórico continua guardado.',
      confirmar: 'Excluir',
    });
    if (!ok) return;
    try {
      await arquivarCard(card.id);
      aoFechar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui excluir.');
    }
  }
}
